import React from 'react';

/**
 * Animated skeleton placeholder. Use it INSTEAD of a centered spinner when
 * the surrounding layout already implies the shape of the missing data
 * (e.g. table rows, card grids, form sections). Skeletons keep the layout
 * stable while the data loads — no content reflow when the real data
 * arrives — which feels faster than a spinner that pops in and out.
 *
 * Honours `prefers-reduced-motion`: the shimmer is replaced by a steady
 * tone when the user has motion suppressed.
 */
export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
  /** Mark inline so it sits next to text instead of taking a full block. */
  inline?: boolean;
}

const ROUND_MAP: Record<NonNullable<SkeletonProps['rounded']>, string> = {
  sm:   'rounded',
  md:   'rounded-md',
  lg:   'rounded-lg',
  xl:   'rounded-xl',
  full: 'rounded-full',
};

export function Skeleton({
  width,
  height = '0.75rem',
  rounded = 'md',
  className = '',
  inline,
}: SkeletonProps) {
  const style: React.CSSProperties = {
    width: width ?? (inline ? '8ch' : '100%'),
    height,
  };
  return (
    <span
      aria-hidden="true"
      className={`skeleton ${inline ? 'inline-block align-middle' : 'block'} ${ROUND_MAP[rounded]} ${className}`}
      style={style}
    />
  );
}

/**
 * Pre-shaped skeleton row matching the WorkTray table layout. Each row
 * shows a leading checkbox-shaped square + N text-shaped bars + a trailing
 * action button pill. `columns` controls the bar count so we mirror the
 * caller's actual table width.
 */
export function SkeletonTableRow({ columns = 5, withActions = true }: { columns?: number; withActions?: boolean }) {
  const bars = Array.from({ length: columns });
  return (
    <tr>
      {bars.map((_, i) => (
        // `min-h-[1.5rem]` reserves the same content height a real cell takes
        // (a text line / status badge), so the row doesn't grow taller when the
        // data arrives — the thin bar stays vertically centered inside it.
        <td key={i} className="px-4 py-3 align-middle">
          <div className="flex items-center min-h-[2.125rem]">
            <Skeleton width={i === 0 ? '70%' : `${50 + (i * 7) % 40}%`} height="0.875rem" />
          </div>
        </td>
      ))}
      {withActions ? (
        <td className="px-4 py-3 w-px align-middle">
          {/* Match the real row-action button height (btn-themed ≈ 2.125rem)
              so the actions cell — the tallest in the row — doesn't make the
              data row grow past the skeleton. */}
          <div className="flex items-center">
            <Skeleton width="3.5rem" height="2.125rem" rounded="lg" />
          </div>
        </td>
      ) : null}
    </tr>
  );
}

/**
 * Stack of `count` skeleton rows. Default 5 — enough to look like a real
 * table without scrolling on most viewports.
 */
export function SkeletonTableBody({ count = 5, columns = 5, withActions = true }: { count?: number; columns?: number; withActions?: boolean }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonTableRow key={i} columns={columns} withActions={withActions} />
      ))}
    </>
  );
}

export default Skeleton;
